import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";

import { sentryService } from "../sentry";
import { visitKeyAt } from "./useNavigableFlow";

const FLOW_TIMELINE_STATE_KEY = "__medbillaiFlowTimeline";

const LEAVE_PROMPT = "You are about to leave the app. Continue?";

type FlowTimelineHistoryEntry = {
  sessionId: string;
  visitKey: string;
  index: number;
};

/**
 * `history.go` resolves on a later task, so between a requested move and its
 * popstate the cursor sits on no entry the hook can name. `to` is the only way
 * to recognise a landing whose stamp another history writer wiped.
 */
type BrowserCursor =
  | { status: "unclaimed" }
  | { status: "anchored"; entry: FlowTimelineHistoryEntry }
  | { status: "traversing"; to: FlowTimelineHistoryEntry }
  | { status: "departed"; resumeVisitKey: string };

type BrowserTimelineSyncProps = {
  /** Index-aligned with the flow timeline; `activeIndex` indexes into these. */
  visitKeys: readonly string[];
  activeIndex: number;
  canGoBack: boolean;
  /** Returns the index the flow stands on after the hop. */
  goBack: () => number;
  canGoForward: boolean;
  goToIndex: (index: number) => void;
};

const readTimelineEntry = (
  state: { [FLOW_TIMELINE_STATE_KEY]?: unknown } | null,
): FlowTimelineHistoryEntry | null => {
  const entry = state?.[FLOW_TIMELINE_STATE_KEY];
  if (
    typeof entry !== "object" ||
    entry === null ||
    !("sessionId" in entry) ||
    typeof entry.sessionId !== "string" ||
    !("visitKey" in entry) ||
    typeof entry.visitKey !== "string" ||
    !("index" in entry) ||
    typeof entry.index !== "number"
  ) {
    return null;
  }
  return {
    sessionId: entry.sessionId,
    visitKey: entry.visitKey,
    index: entry.index,
  };
};

// A debug panel outside this app reads the entry by hand, so the id stays short.
const newSessionId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)), byte =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

const historyStateWithEntry = (entry: FlowTimelineHistoryEntry) => ({
  ...(window.history.state ?? {}),
  [FLOW_TIMELINE_STATE_KEY]: entry,
});

/**
 * Keeps a browser history cursor aligned with a flow timeline. Browser Back
 * and Forward move the existing flow cursor; a new visit uses pushState, which
 * makes the browser discard any forward branch automatically.
 */
export function useBrowserTimelineSync({
  visitKeys,
  activeIndex,
  canGoBack,
  goBack,
  canGoForward,
  goToIndex,
}: BrowserTimelineSyncProps) {
  // Per page load: an id stable across loads makes an earlier load's entries
  // read as ours. `randomUUID` is absent on insecure non-localhost origins,
  // where `getRandomValues` is still there.
  const sessionIdRef = useRef(newSessionId());
  const cursorRef = useRef<BrowserCursor>({ status: "unclaimed" });
  const stampedIndexRef = useRef(new Map<string, number>());

  // Every entry the flow pushes inherits the page's own `__N`, which is the
  // only thing Next's Pages Router checks before handling a popstate — it then
  // replaces the whole history state and takes the flow's stamp with it. This
  // stands the router down for the flow's own entries instead of hiding them:
  // a Back arriving from another route still carries a different `as`, where
  // Next does have a page to restore.
  const router = useRouter();
  useEffect(() => {
    router.beforePopState(
      state =>
        !(FLOW_TIMELINE_STATE_KEY in state) || router.asPath !== state.as,
    );
    // One slot per router with no getter, so teardown can only restore Next's
    // permissive default rather than a previous owner.
    return () => router.beforePopState(() => true);
  }, [router]);

  const syncBrowser = useCallback(() => {
    const activeKey = visitKeys[activeIndex];
    if (activeKey === undefined) {
      return;
    }
    let cursor = cursorRef.current;
    if (cursor.status === "departed") {
      return;
    }
    if (cursor.status === "traversing") {
      if (readTimelineEntry(window.history.state) !== null) {
        // The cursor still stands on an entry the hook stamped, so the move it
        // asked for has not happened yet and its popstate is still coming.
        return;
      }
      // The browser had nowhere to go, so no popstate will ever end this
      // traversal. `to` is the last entry the hook can name.
      cursor = { status: "anchored", entry: cursor.to };
      cursorRef.current = cursor;
    }
    const stamp = (visitKey: string, index: number) => {
      const entry = { sessionId: sessionIdRef.current, visitKey, index };
      cursorRef.current = { status: "anchored", entry };
      stampedIndexRef.current.set(visitKey, index);
      return historyStateWithEntry(entry);
    };

    if (cursor.status === "unclaimed") {
      window.history.replaceState(stamp(activeKey, activeIndex), "");
      return;
    }
    if (readTimelineEntry(window.history.state) === null) {
      // The app router's hydration replaceState overwrites the whole state
      // object, and an anchored cursor is the only evidence that the entry
      // underneath is still the one the flow stands on.
      window.history.replaceState(historyStateWithEntry(cursor.entry), "");
    }
    if (cursor.entry.visitKey === activeKey) {
      return;
    }

    const traverseTo = (visitKey: string, index: number) => {
      const to = { sessionId: sessionIdRef.current, visitKey, index };
      cursorRef.current = { status: "traversing", to };
      // pushState here would delete every forward entry.
      window.history.go(index - cursor.entry.index);
    };

    const stampedIndex = stampedIndexRef.current.get(activeKey);
    if (stampedIndex !== undefined) {
      traverseTo(activeKey, stampedIndex);
      return;
    }

    const anchored = visitKeys.indexOf(cursor.entry.visitKey);
    if (anchored < 0) {
      // A rewind keeps its later visits, but the branch taken from it drops
      // them, and the cursor stands on one. Rebuild from the newest visit both
      // sides still hold; index 0 always carries a stamp, so the walk lands.
      for (let index = activeIndex - 1; index >= 0; index -= 1) {
        const ancestorKey = visitKeyAt(visitKeys, index);
        const ancestorIndex = stampedIndexRef.current.get(ancestorKey);
        if (ancestorIndex !== undefined) {
          traverseTo(ancestorKey, ancestorIndex);
          return;
        }
      }
      return;
    }

    // Visits the flow reached inside one batched render share this effect run,
    // so every visit between the anchor and the active one needs its entry.
    for (let index = anchored + 1; index <= activeIndex; index += 1) {
      window.history.pushState(stamp(visitKeyAt(visitKeys, index), index), "");
    }
  }, [activeIndex, visitKeys]);

  useEffect(() => {
    syncBrowser();
  }, [syncBrowser]);

  const jumpBack = useCallback(
    (steps = 1) => {
      if (!canGoBack) {
        return;
      }
      const targetIndex = activeIndex - steps;
      if (targetIndex < 0) {
        sentryService.captureMessage(
          "useBrowserTimelineSync: jumpBack() target does not exist",
          {
            level: "warning",
            extra: { activeIndex, steps, targetIndex },
          },
        );
        return;
      }
      // One hop per entry, so every screen in between runs its own Back handler
      // and its own guard. `steps` bounds the walk: a Back handler is free to
      // return a forward action, which would otherwise never reach the target.
      let index = activeIndex;
      for (let step = 0; step < steps && index > targetIndex; step += 1) {
        const landed = goBack();
        if (landed >= index) {
          // The screen refused Back — it warned once for this hop, and spending
          // the remaining steps would only repeat the warning.
          return;
        }
        index = landed;
      }
    },
    [activeIndex, canGoBack, goBack],
  );

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const cursor = cursorRef.current;
      const entry = readTimelineEntry(event.state);
      const landed = entry?.sessionId === sessionIdRef.current ? entry : null;

      if (
        cursor.status === "departed" &&
        landed?.visitKey !== cursor.resumeVisitKey
      ) {
        return;
      }

      if (landed === null) {
        if (cursor.status === "traversing" && entry === null) {
          // The hook asked for this move, so a landing it cannot read is the
          // entry it aimed at with the stamp wiped off, not a foreign one.
          cursorRef.current = { status: "anchored", entry: cursor.to };
          syncBrowser();
          return;
        }
        if (window.confirm(LEAVE_PROMPT)) {
          // Same-document departures keep the flow mounted. Earlier owned
          // entries must not re-arm it before the member returns to this visit.
          cursorRef.current = {
            status: "departed",
            resumeVisitKey: visitKeyAt(visitKeys, activeIndex),
          };
          return;
        }
        if (cursor.status === "anchored") {
          cursorRef.current = { status: "traversing", to: cursor.entry };
        }
        // Every pushState the flow made discarded the entries ahead of it, so
        // an entry it cannot identify sits before the one it started on.
        window.history.forward();
        return;
      }

      const requested = cursor.status === "traversing";
      cursorRef.current = { status: "anchored", entry: landed };
      if (requested) {
        // The flow can have moved on while the traversal was in flight.
        syncBrowser();
        return;
      }
      if (landed.visitKey === visitKeys[activeIndex]) {
        return;
      }

      // A visit dropped by branching after a rewind keeps its browser entry, so
      // the entry alone is not proof the flow can still go there.
      const target = visitKeys.indexOf(landed.visitKey);
      const backward = target >= 0 && target < activeIndex;
      const accepted = target >= 0 && (backward ? canGoBack : canGoForward);
      if (!accepted) {
        // Back is the member's only way out of a screen that refuses it, so a
        // refused back means they want to leave the app.
        if (backward && window.confirm(LEAVE_PROMPT)) {
          cursorRef.current = {
            status: "departed",
            resumeVisitKey: visitKeyAt(visitKeys, activeIndex),
          };
          return;
        }
        // The cursor is on the refused entry now, and the next pushState would
        // branch from there unless synchronising moves it back.
        syncBrowser();
        return;
      }

      if (backward) {
        // One accepted transition per entry crossed. Jumping straight to the
        // target would skip every Back handler and guard in between, which the
        // browser's history menu makes an ordinary press away. Forward is safe
        // to move directly: `goForward` runs no handlers, so it skips nothing.
        jumpBack(activeIndex - target);
        return;
      }
      goToIndex(target);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [
    activeIndex,
    canGoBack,
    canGoForward,
    goToIndex,
    jumpBack,
    syncBrowser,
    visitKeys,
  ]);

  return {
    handleBack: () => jumpBack(),
    handleForward: () => {
      if (canGoForward) {
        goToIndex(activeIndex + 1);
      }
    },
    jumpBack,
  };
}
